import { memo } from 'react';
import { X } from 'lucide-react';

const InputField = ({ label, value, onChange, type = 'number', suffix = '', min = 0 }) => (
  <div className="space-y-2">
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <div className="relative">
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        min={min}
        step={type === 'number' && value < 100 ? '0.1' : '1'}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
      {suffix && (
        <span className="absolute right-3 top-2.5 text-gray-500 text-sm">{suffix}</span>
      )}
    </div>
  </div>
);

export const ProjectionsModal = memo(({ isOpen, onClose, inputs, updateInput, resetInputs }) => {
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Adjust Projection Inputs</h3>
                <p className="text-sm text-gray-600 mt-1">Modify values to see updated projections</p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Traffic & Conversion Section */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-4">Traffic & Conversion Rates</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    label="Monthly Web Traffic"
                    value={inputs.traffic}
                    onChange={(val) => updateInput('traffic', val)}
                  />
                  
                  <InputField
                    label="Visitor to MQL Conversion Rate"
                    value={inputs.visitorConversionRate}
                    onChange={(val) => updateInput('visitorConversionRate', val)}
                    suffix="%"
                  />
                  
                  <InputField
                    label="MQL to SQL Conversion Rate"
                    value={inputs.mqlToSqlRate}
                    onChange={(val) => updateInput('mqlToSqlRate', val)}
                    suffix="%"
                  />
                  
                  <InputField
                    label="Show Up Rate (SQL to Meeting)"
                    value={inputs.showUpRate}
                    onChange={(val) => updateInput('showUpRate', val)}
                    suffix="%"
                  />
                  
                  <InputField
                    label="Closing Rate"
                    value={inputs.closingRate}
                    onChange={(val) => updateInput('closingRate', val)}
                    suffix="%"
                  />
                </div>
              </div>

              {/* Financial Section */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-4">Financial Metrics</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    label="Average Sale Value"
                    value={inputs.avgSaleValue}
                    onChange={(val) => updateInput('avgSaleValue', val)}
                    suffix="$"
                  />
                  
                  <InputField
                    label="Cost Per Lead (CPL)"
                    value={inputs.costPerLead}
                    onChange={(val) => updateInput('costPerLead', val)}
                    suffix="$"
                  />
                  
                  <InputField
                    label="Total Ad Spend"
                    value={inputs.adSpend}
                    onChange={(val) => updateInput('adSpend', val)}
                    suffix="$"
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-between">
              <button
                onClick={resetInputs}
                className="px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg text-sm font-medium text-gray-700 transition-colors"
              >
                Reset to Defaults
              </button>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium text-white transition-colors"
              >
                Apply Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
});

ProjectionsModal.displayName = 'ProjectionsModal';